import {
  type Au06Input,
  Au06InputSchema,
  type Au06Output,
  type Au06RuleSet,
  type RepaymentBand,
  type RepaymentIncomeComponents,
} from "./types.js";

/** Sum the ATO repayment-income components into a single repayment income. */
export function sumRepaymentIncome(c: RepaymentIncomeComponents): number {
  return (
    c.taxableIncome +
    c.netInvestmentLoss +
    c.reportableFringeBenefits +
    c.reportableSuperContributions +
    c.exemptForeignEmploymentIncome
  );
}

/** Pick the band whose `[from, to)` contains `income`. */
function bandFor(bands: readonly RepaymentBand[], income: number): RepaymentBand {
  for (const band of bands) {
    if (income >= band.from && (band.to === null || income < band.to)) {
      return band;
    }
  }
  // Bands are authored to cover [0, ∞); a miss means a malformed ruleset.
  throw new Error(`no AU-06 band matched repayment income ${income}`);
}

/** Inclusive label for a band, e.g. "$67,001–$125,000" or "$179,286 and over". */
function bandLabel(band: RepaymentBand): string {
  const fmt = (n: number) => `$${n.toLocaleString("en-AU")}`;
  if (band.rate === 0) return `Below $${(67_000).toLocaleString("en-AU")}`;
  if (band.to === null) return `${fmt(band.from)} and over`;
  // `to` is exclusive; the published table is inclusive, so show `to − 1`.
  return `${fmt(band.from)}–${fmt(band.to - 1)}`;
}

/**
 * Compulsory repayment under the FY26 marginal model.
 *
 * For a normal band: repayment = baseAmount + rate × (income − thresholdFloor),
 * where the threshold floor is the band's `from − 1` (the published table reads
 * "Nc for each $1 over $X", and the band starts at $X+1). The top band is NOT
 * marginal — it charges `rate` × whole repayment income.
 */
function repaymentFor(band: RepaymentBand, income: number): number {
  if (band.rate === 0) return 0;
  if (band.wholeIncome) {
    return Math.round(income * band.rate);
  }
  const thresholdFloor = band.from - 1;
  const aboveThreshold = income - thresholdFloor;
  return Math.round(band.baseAmount + band.rate * aboveThreshold);
}

/**
 * Calculate AU-06 (HECS/HELP compulsory repayment) for one assessment. Pure:
 * same inputs + ruleset always yield the same output. Validates at the
 * boundary so malformed payloads fail loudly rather than producing junk.
 */
export function calculateAu06(
  rawInput: Au06Input,
  rules: Au06RuleSet,
): Au06Output {
  const input = Au06InputSchema.parse(rawInput);

  const repaymentIncome =
    input.repaymentIncome ??
    // `refine` guarantees one of the two is present.
    sumRepaymentIncome(input.income as RepaymentIncomeComponents);

  const band = bandFor(rules.bands, repaymentIncome);
  const compulsoryRepayment = repaymentFor(band, repaymentIncome);
  const repaymentDue = compulsoryRepayment > 0;

  // The next band's floor (and how far away the user is), unless we're already
  // in the top band.
  const idx = rules.bands.indexOf(band);
  const next = rules.bands[idx + 1];
  const nextBand =
    next && band.to !== null
      ? { at: band.to, away: band.to - repaymentIncome }
      : null;

  const fractionOfBalance =
    input.loanBalance !== undefined && input.loanBalance > 0
      ? compulsoryRepayment / input.loanBalance
      : null;

  return {
    repaymentIncome,
    compulsoryRepayment,
    marginalRate: band.rate,
    thresholdBand: bandLabel(band),
    repaymentDue,
    nextBand,
    fractionOfBalance,
  };
}
